import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   computed,
   inject,
   Injector,
   OnDestroy,
   signal,
   ViewEncapsulation,
} from '@angular/core';
import {
   FormControl,
   FormGroup,
   NG_VALUE_ACCESSOR,
   UntypedFormControl,
   UntypedFormGroup,
   Validators,
} from '@angular/forms';
import {
   LcsAdvancedMultiSelectDatasource,
   LcsAdvancedMultiSelectDatasourceId,
   LcsAdvancedMultiSelectOpenDialogEvent,
   LcsAdvancedMultiSelectReadonlyStateType,
   LcsAdvancedMultiSelectStateEventPayloads,
   LcsAdvancedMultiSelectStateInitEventPayload,
   LcsAdvancedMultiSelectTriggerConfig,
   LcsAdvancedMultiSelectTriggerFormGroup,
} from '@lcs/shared/advanced-multi-select';
import { LcsSelectWithAllDisplayTypes } from '@lcs/shared/select-with-all';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ExtensionGroupOption } from 'src/app/common-components/entity-select/extension-group/models/extension-group-option';
import { extensionGroupOptionActions } from 'src/app/common-components/entity-select/extension-group/state/extension-group-option.actions';
import { extensionGroupOptionSelectors } from 'src/app/common-components/entity-select/extension-group/state/extension-group-option.reducer';
import { extensionOptionActions } from 'src/app/common-components/entity-select/extension/state/extension-option.actions';
import { extensionOptionSelectors } from 'src/app/common-components/entity-select/extension/state/extension-option.reducer';
import { ExtensionOption } from 'src/app/pages/extension-register/models/extension-option.interface';
import { getSelectedAccountId } from 'src/app/store/select-account/select-account.selector';
import { ReportParameterComponent } from '../../report-parameter-list/report-parameter-component';
import { ReportParameterConfig } from '../../report-parameter-list/report-parameter.config';

export enum ExtensionReportParameterTabs {
   Extension,
   Group,
}

type AggregateExtensionOption = { type: 'extension'; item: ExtensionOption };
type AggregateExtensionGroupOption = {
   type: 'group';
   item: ExtensionGroupOption;
};
type AggregateOption = AggregateExtensionOption | AggregateExtensionGroupOption;

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            multi: true,
            provide: NG_VALUE_ACCESSOR,
            useExisting: ExtensionsReportParameterComponent,
        },
    ],
    selector: 'ndt-extensions-report-parameter',
    templateUrl: './extensions-report-parameter.component.html',
    styleUrls: ['./extensions-report-parameter.component.scss'],
    standalone: false
})
export class ExtensionsReportParameterComponent implements OnDestroy, ReportParameterComponent {
   form: UntypedFormGroup;
   selectedAccountId: number | null;
   searchControl = new UntypedFormControl('');

   private destroyed$ = new Subject<void>();

   config: ReportParameterConfig;

   extensionAndGroupEntityName = 'Extensions/Groups';

   extensionEntityName = 'Extensions';

   groupEntityName = 'Extension Groups';

   readonly store = inject(Store);
   readonly cd = inject(ChangeDetectorRef);
   readonly injector = inject(Injector);

   readonly allExtensions = this.store.selectSignal(extensionOptionSelectors.selectAll);
   readonly extensions = this.store.selectSignal(extensionOptionSelectors.selectFilteredItems);
   readonly extensionsCallState = this.store.selectSignal(extensionOptionSelectors.selectCallState);
   readonly allGroups = this.store.selectSignal(extensionGroupOptionSelectors.selectAll);
   readonly groups = this.store.selectSignal(extensionGroupOptionSelectors.selectFilteredItems);
   readonly groupsCallState = this.store.selectSignal(extensionGroupOptionSelectors.selectCallState);

   readonly selectedDatasourceId = signal<LcsAdvancedMultiSelectDatasourceId>(ExtensionReportParameterTabs.Extension);

   readonly aggregateControl = new FormControl<AggregateOption[]>([], {
      nonNullable: true,
   });

   readonly triggerConfig = computed<LcsAdvancedMultiSelectTriggerConfig<AggregateOption>>(() => {
      return {
         displayOptions: {
            label: this.extensionAndGroupEntityName,
            displayType: LcsSelectWithAllDisplayTypes.Label,
            displayWithFn: (
               items: AggregateOption[],
               state: LcsAdvancedMultiSelectReadonlyStateType<AggregateOption>
            ) => {
               const extensions = items.filter((i) => i.type === 'extension');
               const groups = items.filter((i) => i.type === 'group');
               return `${extensions.length} Extensions - ${groups.length} Groups`;
            },
            hasAllCheckbox: true,
         },
         group: new LcsAdvancedMultiSelectTriggerFormGroup<AggregateOption>(
            {
               items: this.aggregateControl,
               all: new FormControl(this.form.controls.all.value),
            },
            {
               validators: this.form.hasValidator(Validators.required) ? [Validators.required] : [],
            }
         ),
         syncOn: 'save',
      };
   });

   readonly selectedItems = signal<AggregateOption[]>([]);

   readonly datasources = computed(() => {
      return [this.extensionDataSource(), this.groupDataSource()];
   });

   readonly groupDataSource = computed<LcsAdvancedMultiSelectDatasource<AggregateOption>>(() => {
      return {
         id: ExtensionReportParameterTabs.Group,
         label: this.groupEntityName,
         items: (this.availableGroups() ?? []).map((g) => ({
            type: 'group',
            item: g,
         })),
         selectedItems: this.selectedItems(),
         itemTrackBy: this.aggregateOptionTrackBy,
         displayOptions: {
            displayWithFn: (item: AggregateOption) => this.aggregateDisplayWithFn(item),
         },
         callState: this.groupsCallState(),
      };
   });

   readonly extensionDataSource = computed<LcsAdvancedMultiSelectDatasource<AggregateOption>>(() => {
      return {
         id: ExtensionReportParameterTabs.Extension,
         label: this.extensionEntityName,
         items: (this.availableExtensions() ?? []).map((e) => ({
            type: 'extension',
            item: e,
         })),
         selectedItems: this.selectedItems(),
         itemTrackBy: this.aggregateOptionTrackBy,
         displayOptions: {
            displayWithFn: (item: AggregateOption) => this.aggregateDisplayWithFn(item),
         },
         callState: this.extensionsCallState(),
      };
   });

   readonly availableExtensions = computed(() => {
      const extensions = this.extensions();
      return extensions;
   });

   readonly availableGroups = computed(() => {
      const groups = this.groups();
      return groups;
   });

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   onAllChange(all: boolean) {
      this.form.controls.all.setValue(all);
   }

   onInit(e: LcsAdvancedMultiSelectStateInitEventPayload<AggregateOption>) {
      const extensions = this.form.controls.extensions;
      const parentControls: FormGroup[] = this.form.parent?.controls as FormGroup[];
      const extensionGroupControl = parentControls.find((fg) => fg.controls['extensionGroups']);
      if (extensions.value && extensionGroupControl?.controls.extensionGroups.value) {
         const aggregateExtensions: AggregateOption[] = extensions.value.map((e): AggregateOption => {
            return {
               type: 'extension',
               item: e,
            };
         });
         const aggregateGroups: AggregateOption[] = extensionGroupControl?.controls.extensionGroups.value.map(
            (g): AggregateOption => {
               return {
                  type: 'group',
                  item: g,
               };
            }
         );
         this.selectedItems.set([...aggregateExtensions, ...aggregateGroups]);
         this.cd.detectChanges();
      }

      this.store
         .select(getSelectedAccountId)
         .pipe(takeUntil(this.destroyed$))
         .subscribe((selectedAccountId) => {
            if (selectedAccountId) {
               this.selectedAccountId = selectedAccountId;
               this.form.controls.accountId.setValue(this.selectedAccountId);
               this.store.dispatch(
                  extensionOptionActions.load({
                     additionalFields: [],
                     filterOverrides: [`accountId = ${selectedAccountId}`, 'isArchived = false'],
                  })
               );
               this.store.dispatch(
                  extensionGroupOptionActions.load({
                     additionalFields: [],
                     filterOverrides: [`accountId = ${selectedAccountId}`],
                  })
               );
            }
         });
   }

   onOpen(e: LcsAdvancedMultiSelectOpenDialogEvent<AggregateOption>) {
      this.store.dispatch(extensionOptionActions.searchReset());
      this.store.dispatch(extensionGroupOptionActions.searchReset());
   }

   onSave(e: LcsAdvancedMultiSelectStateEventPayloads<AggregateOption>['save']) {
      this.selectedDatasourceId.set(e.state.selectedDatasource().id);

      const selectedItems = e.state.selectedItems();
      const parentControls: FormGroup[] = this.form.parent?.controls as FormGroup[];
      const extensionGroupControl = parentControls.find((fg) => fg.controls['extensionGroups']);
      this.form.controls.extensions.setValue(selectedItems.filter((i) => i.type === 'extension').map((i) => i.item));
      if (extensionGroupControl) {
         extensionGroupControl.controls.extensionGroups.setValue(
            selectedItems.filter((i) => i.type === 'group').map((i) => i.item)
         );
      }
      this.form.controls.all.setValue(selectedItems.length === 0);
   }

   onSearch(e: LcsAdvancedMultiSelectStateEventPayloads<AggregateOption>['search']) {
      this.store.dispatch(extensionOptionActions.search({ searchTerm: e.searchText }));
      this.store.dispatch(extensionGroupOptionActions.search({ searchTerm: e.searchText }));
   }

   onSelectedItemsChange(e: LcsAdvancedMultiSelectStateEventPayloads<AggregateOption>['selectedItemsChange']) {
      this.selectedItems.set(e.state.selectedItems());
   }

   onClose(e: LcsAdvancedMultiSelectStateEventPayloads<AggregateOption>['close']) {
      // Reset to the combination of the group and extensions form controls
      this.selectedItems.set(this.aggregateControl.value);
      this.form.controls.extensions.setValue(
         this.aggregateControl.value?.filter((i) => i.type === 'extension').map((i) => i.item)
      );
      const parentControls: FormGroup[] = this.form.parent?.controls as FormGroup[];
      const extensionGroupControl = parentControls.find((fg) => fg.controls['extensionGroups']);
      if (extensionGroupControl) {
         extensionGroupControl.controls.extensionGroups.setValue(
            this.aggregateControl.value.filter((i) => i.type === 'group').map((i) => i.item)
         );
      }
   }

   isAggregateItemSelected(aggregateItem: AggregateOption) {
      // Both usersDatasource and teamsDatasource have same selectedItems
      return this.extensionDataSource().selectedItems?.some((i) => {
         return this.aggregateOptionTrackBy(i) === this.aggregateOptionTrackBy(aggregateItem);
      });
   }

   private aggregateOptionTrackBy = (a: AggregateOption) =>
      a.type === 'extension' ? this.extensionTrackBy(a.item) : this.groupTrackBy(a.item);
   private extensionTrackBy = (extension: ExtensionOption) => extension.extensionId;
   private groupTrackBy = (group: ExtensionGroupOption) => group.extensionGroupId;
   private aggregateDisplayWithFn: (item: AggregateOption) => string = (item) => {
      if (item.type === 'extension') {
         return `${item.item.number} - ${item.item.name}`;
      } else {
         return `${item.item.name}`;
      }
   };
}
